<template>
  <div class="trip-details">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-left">
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-btn icon @click="callBackRoute" v-bind="attrs" v-on="on" class="mr-3">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
          </template>
          <span>Back</span>
        </v-tooltip>
        <h2 class="trip-title">Emergency Trip Details – {{ tripDetails.trip_id }}</h2>
        <span class="status-chip" :class="getStatusClass(tripDetails.status)">
          {{ tripDetails.status }}
        </span>

        <v-menu open-on-hover right offset-y v-if="tripDetails.id">
          <template v-slot:activator="{ on, attrs }">
            <button class="icon-btn icon-btn-outline icon-btn-primary" title="Route Address" v-on="on" v-bind="attrs">
              <v-icon x-small class="mr-1">mdi-map-marker-distance</v-icon>
              <span>Route Address</span>
            </button>
          </template>
          <v-card>
            <v-card-title class="secondary white--text text-subtitle-1 font-weight-medium py-1">
              Route Address
            </v-card-title>
            <v-card-text class="mt-2">
              <table class="Normal-Table">
                <tbody>
                  <tr v-if="tripDetails.source_location_name">
                    <th>Source Location</th>
                    <th class="px-2">:</th>
                    <td>{{ tripDetails.source_location_name }}</td>
                  </tr>
                  <tr v-if="tripDetails.mid_location_name">
                    <th>Mid Point Location</th>
                    <th class="px-2">:</th>
                    <td>{{ tripDetails.mid_location_name }}</td>
                  </tr>
                  <tr v-if="tripDetails.destination_location_name">
                    <th>Destination Location</th>
                    <th class="px-2">:</th>
                    <td>{{ tripDetails.destination_location_name }}</td>
                  </tr>
                </tbody>
              </table>
            </v-card-text>
          </v-card>
        </v-menu>

        <v-menu open-on-hover right offset-y v-if="tripContainers.length">
          <template v-slot:activator="{ on, attrs }">
            <button
              class="icon-btn icon-btn-outline icon-btn-success"
              title="Container Information"
              v-on="on"
              v-bind="attrs"
            >
              <v-icon x-small class="mr-1">mdi-train-car-container</v-icon>
              <span>Container Info</span>
            </button>
          </template>
          <v-card>
            <v-card-title class="secondary white--text text-subtitle-1 font-weight-medium py-1">
              Container Information
            </v-card-title>
            <v-card-text class="mt-2">
              <table class="Normal-Table">
                <thead>
                  <tr>
                    <th class="pr-4">Container No</th>
                    <th class="pr-4">DA No</th>
                    <th>Route</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in tripContainers" :key="i">
                    <td class="pr-4">{{ item.container_no }}</td>
                    <td class="pr-4">{{ item.da_no }}</td>
                    <td>{{ item.route || "-" }}</td>
                  </tr>
                </tbody>
              </table>
            </v-card-text>
          </v-card>
        </v-menu>
        <button v-else class="icon-btn icon-btn-outline icon-btn-success" title="Container Information" disabled>
          <v-icon x-small class="mr-1">mdi-train-car-container</v-icon>
          <span>Container Info</span>
        </button>
      </div>

      <div class="header-right">
        <v-btn class="primary-btn" color="secondary" small @click="showLegDialog = true">
          <v-icon left>mdi-truck-delivery</v-icon>
          Legs
        </v-btn>
        <v-btn
          class="primary-btn"
          color="primary"
          small
          @click="$bus.$emit('openTripChat', tripDetails.id)"
        >
          <v-icon left>mdi-chat</v-icon>
          Open Chat
        </v-btn>
        <v-btn color="primary" small class="primary-btn" @click="showTripLogs = true">
          <v-icon left>mdi-history</v-icon>
          Logs
        </v-btn>
        <v-btn color="primary" small class="primary-btn" @click="viewCheckList = true">
          <v-icon class="pl-2">mdi-format-list-checks</v-icon>
          View Checklist
        </v-btn>
      </div>
    </div>

    <!-- Trip Summary Section -->
    <v-expansion-panels v-model="summaryPanel" class="mb-4">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <span class="font-weight-bold">
            <v-icon left color="primary" small>mdi-view-grid-outline</v-icon>
            Trip Summary
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-label">Source Location</div>
              <div class="summary-value">{{ tripDetails.source_location_name || "N/A" }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Mid Location</div>
              <div class="summary-value">{{ tripDetails.mid_location_name || "N/A" }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Trip Date</div>
              <div class="summary-value">{{ tripDetails.trip_date || "N/A" }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Est Start Date &amp; Time</div>
              <div class="summary-value">{{ estStartDateTime }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Est. End Date &amp; Time</div>
              <div class="summary-value">{{ estEndDateTime }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Driver Name</div>
              <div
                class="summary-value"
                :class="{ 'summary-link primary--text': tripDetails.driver_id }"
                @click="goToDriver"
              >
                {{ tripDetails.driver_name || "N/A" }}
              </div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Vehicle</div>
              <div
                class="summary-value"
                :class="{ 'summary-link primary--text': tripDetails.vehicle_id }"
                @click="goToVehicle"
              >
                {{ tripDetails.vehicle_number || "N/A" }}
              </div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Trailer</div>
              <div class="summary-value">{{ tripDetails.trailer_1_number || "N/A" }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Depot</div>
              <div class="summary-value">{{ tripDetails.depot || "N/A" }}</div>
            </div>
          </div>

          <v-divider class="my-3" />

          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-label">Actual Trip Start</div>
              <div class="summary-value">
                {{ tripDetails.trip_start ? formatDateTime(tripDetails.trip_start) : "N/A" }}
              </div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Actual Trip End</div>
              <div class="summary-value">
                {{ tripDetails.trip_end ? formatDateTime(tripDetails.trip_end) : "N/A" }}
              </div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Trip Start KM</div>
              <div class="summary-value">{{ tripDetails.trip_start_km || "N/A" }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Trip End KM</div>
              <div class="summary-value">{{ tripDetails.trip_end_km || "—" }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Channel</div>
              <div class="summary-value">{{ tripDetails.completion_agent || "N/A" }}</div>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Main Content: Geofence Log + Milestones -->
    <v-row>
      <v-col cols="12" md="7">
        <v-card class="fill-height content-card">
          <v-card-title class="section-title">
            <v-icon left color="primary">mdi-map-marker-radius-outline</v-icon>
            Trip Geofence Event Log
          </v-card-title>
          <v-card-text>
            <v-timeline v-if="geofenceEvents.length" dense>
              <v-timeline-item
                v-for="(event, i) in geofenceEvents"
                :key="i"
                :color="event.dot_color"
                small
              >
                <div class="d-flex align-center">
                  <strong>{{ event.title }}</strong>
                  <v-chip x-small outlined :color="event.dot_color" class="ml-2 font-weight-bold">
                    {{ event.type }}
                  </v-chip>
                </div>
                <div class="caption grey--text text--darken-1">
                  <v-icon x-small class="mr-1">mdi-map-marker-outline</v-icon>
                  {{ event.location }}
                </div>
                <div class="caption grey--text">
                  {{ event.time }}
                  <v-chip x-small outlined color="blue-grey" class="ml-1" v-if="event.duration">
                    {{ event.duration }}
                  </v-chip>
                </div>
              </v-timeline-item>
            </v-timeline>
            <p v-else class="caption grey--text text-center mt-2">
              No geofence events recorded for this trip yet.
            </p>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card class="fill-height content-card">
          <v-card-title class="section-title d-flex align-center">
            <v-icon left color="primary">mdi-flag-outline</v-icon>
            <span>Trip Milestones</span>
            <v-spacer />
            <MultiSelect
              v-if="tripDetails.das && tripDetails.das.length"
              :items="tripDetails.das"
              itemText="da_no"
              itemValue="id"
              v-model="selectedDaId"
              hide-details="auto"
              dense
              outlined
              style="max-width: 140px"
              designClass="mb-0"
            />
          </v-card-title>
          <v-card-text class="pt-2 pb-1">
            <template v-if="selectedDa">
              <p class="overline grey--text mb-1">
                Milestones for {{ selectedDa.da_no }}
                <span v-if="selectedDa.route"> – {{ selectedDa.route }}</span>
              </p>
              <div
                v-for="(milestone, i) in selectedDa.milestones"
                :key="i"
                class="milestone-row"
              >
                <v-icon :color="milestone.timestamp ? 'success' : 'grey'" small>
                  {{ milestone.timestamp ? "mdi-check-circle" : "mdi-checkbox-blank-circle-outline" }}
                </v-icon>
                <div class="ml-3">
                  <div class="font-weight-bold">
                    <v-icon x-small color="red" left>mdi-map-marker</v-icon>
                    {{ milestone.milestone_display }}
                  </div>
                  <div class="caption grey--text">{{ milestone.timestamp || "—" }}</div>
                  <div class="caption">Container: {{ milestone.container_no }}</div>
                </div>
              </div>
              <p v-if="!selectedDa.milestones.length" class="caption grey--text text-center mt-2">
                No milestones recorded for this DA yet.
              </p>
            </template>
            <p v-else class="caption grey--text text-center mt-2">
              No Emergency Order / DA linked to this trip yet.
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <TripLogsDialog
      v-model="showTripLogs"
      :tripDetails="tripDetails"
      @refreshList="getTripDetails"
    />
    <ViewCheckListDialog
      v-model="viewCheckList"
      :tripDetails="tripDetails"
      @refreshList="getTripDetails"
    />
    <TripLegsDialog
      v-model="showLegDialog"
      :tripDetails="tripDetails"
      @refreshList="getTripDetails"
    />
  </div>
</template>
<script>
import TripLogsDialog from "@/components/ModuleBased/dialogForms/TripLogsDialog.vue";
import ViewCheckListDialog from "@/components/ModuleBased/dialogForms/ViewCheckListDialog.vue";
import TripLegsDialog from "@/components/ModuleBased/dialogForms/TripLegsDialog.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    TripLogsDialog,
    ViewCheckListDialog,
    TripLegsDialog,
    MultiSelect,
  },
  data() {
    return {
      tripDetails: {},
      summaryPanel: 0,
      showTripLogs: false,
      viewCheckList: false,
      showLegDialog: false,
      permissions: null,
      selectedDaId: null,
    };
  },
  computed: {
    geofenceEvents() {
      return this.tripDetails.geofence_events || [];
    },
    selectedDa() {
      const das = this.tripDetails.das || [];
      if (!das.length) return null;
      if (!this.selectedDaId) return das[0];
      return das.find((da) => da.id === this.selectedDaId) || das[0];
    },
    estStartDateTime() {
      if (!this.tripDetails.est_start_date) return "N/A";
      return `${this.tripDetails.est_start_date} ${this.tripDetails.est_start_time || ""}`;
    },
    estEndDateTime() {
      if (!this.tripDetails.est_end_date) return "N/A";
      return `${this.tripDetails.est_end_date} ${this.tripDetails.est_end_time || ""}`;
    },
    tripContainers() {
      const das = this.tripDetails.das || [];
      const containerNumbers = new Set();
      const rows = [];
      das.forEach((da) => {
        (da.milestones || []).forEach((milestone) => {
          if (milestone.container_no && !containerNumbers.has(milestone.container_no)) {
            containerNumbers.add(milestone.container_no);
            rows.push({ container_no: milestone.container_no, da_no: da.da_no, route: da.route });
          }
        });
      });
      return rows;
    },
  },
  methods: {
    callBackRoute() {
      this.$router.push("/app/admin/draft-trip/list");
    },
    getStatusClass(status) {
      return {
        "status-active": status === "Active",
        "status-completed": status === "Completed",
        "status-pending": status === "Assigned",
      };
    },
    formatDateTime(dateTimeStr) {
      return dateTimeStr;
    },
    goToDriver() {
      if (!this.tripDetails.driver_id) return;
      const route = this.$router.resolve(
        `/app/admin/asset-management/driver/edit/${this.tripDetails.driver_id}`
      );
      window.open(route.href, "_blank");
    },
    goToVehicle() {
      if (!this.tripDetails.vehicle_id) return;
      const route = this.$router.resolve(
        `/app/admin/asset-management/vehicle/edit/${this.tripDetails.vehicle_id}`
      );
      window.open(route.href, "_blank");
    },
    getTripDetails() {
      this.$bus.$emit("showLoader", true);
      this.$api.draftTrip
        .getDraftTripObject(this.$route.params.id)
        .then((res) => {
          this.tripDetails = res.data;
          if (this.tripDetails.das && this.tripDetails.das.length) {
            this.selectedDaId = this.tripDetails.das[0].id;
          }
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
  },
  watch: {
    "$route.params.id"(newId, oldId) {
      if (newId && newId !== oldId) {
        this.getTripDetails();
      }
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("draft_trip");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this module!",
          color: "error",
        });
        this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
      } else {
        this.permissions = { ...permissions };
        this.getTripDetails();
      }
    }
  },
};
</script>
<style scoped>
.trip-details {
  padding: 16px;
  background-color: #f5f5f5;
  font-family: "Roboto", sans-serif;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 16px 24px;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
  gap: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.header-right {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.container-chip-row {
  max-width: 100%;
}

.trip-title {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: #333;
}

.status-chip {
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.status-active {
  background: #ff9800;
  color: rgb(225, 90, 11);
}

.status-completed {
  background: #4caf50;
  color: white;
}

.status-pending {
  background: #2196f3;
  color: white;
}

.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  position: relative;
  color: #666;
}

.icon-btn:hover {
  background: #f0f0f0;
}

.icon-btn-outline {
  display: inline-flex;
  align-items: center;
  border: 1px solid currentColor;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
}

.icon-btn-outline:hover {
  background: rgba(0, 0, 0, 0.04);
}

.icon-btn-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.icon-btn-primary {
  color: #ff5722;
}

.icon-btn-success {
  color: #4caf50;
}

.primary-btn {
  background: #ff5722;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0 24px;
}

.summary-item {
  min-width: 0;
  padding: 4px 16px 4px 0;
  border-right: 1px solid #e0e0e0;
}

.summary-item:last-child {
  border-right: none;
}

.summary-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  color: #9e9e9e;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 14px;
  font-weight: 600;
  color: #212121;
}

.summary-link {
  cursor: pointer;
}

.section-title {
  background: #f8f9fa;
  font-weight: 600;
}

.content-card {
  min-height: 420px;
}

.milestone-row {
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.milestone-row:last-child {
  border-bottom: none;
}
</style>
